// StatusPageEdit — T092
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getStatusPage, createStatusPage, updateStatusPage,
  getMonitors, type Monitor,
} from '../services/api';

/** Fetch ALL monitors by paginating through every page (server caps at 100/page). */
async function fetchAllMonitors(): Promise<Monitor[]> {
  const all: Monitor[] = [];
  let page = 1;
  const limit = 100; // server max
  while (true) {
    const res = await getMonitors({ page: String(page), limit: String(limit) });
    all.push(...(res.data || []));
    // The API may return totalPages or just total — handle both
    const totalPages = res.pagination?.totalPages ?? Math.ceil((res.pagination?.total ?? 0) / limit);
    if (!res.pagination || totalPages <= 0 || page >= totalPages) break;
    page++;
  }
  return all;
}

export default function StatusPageEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [selectedMonitorIds, setSelectedMonitorIds] = useState<string[]>([]);
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [monitorSearch, setMonitorSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const autoSlugify = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleTitleChange = (val: string) => {
    setTitle(val);
    if (isNew) setSlug(autoSlugify(val));
  };

  /** Monitors filtered by the search input, with selected monitors pinned to top. */
  const filteredMonitors = useMemo(() => {
    const q = monitorSearch.toLowerCase().trim();
    const list = q
      ? monitors.filter((m) => m.name.toLowerCase().includes(q) || m.url.toLowerCase().includes(q))
      : monitors;
    // Pin selected monitors to top for easy visibility
    return [...list].sort((a, b) => {
      const aSelected = selectedMonitorIds.includes(a.id) ? 0 : 1;
      const bSelected = selectedMonitorIds.includes(b.id) ? 0 : 1;
      return aSelected - bSelected || a.name.localeCompare(b.name);
    });
  }, [monitors, monitorSearch, selectedMonitorIds]);

  useEffect(() => {
    const init = async () => {
      try {
        const allMonitors = await fetchAllMonitors();
        setMonitors(allMonitors);
        if (!isNew && id) {
          const page = await getStatusPage(id);
          setTitle(page.title);
          setSlug(page.slug);
          setIsPublished(page.isPublished);
          setSelectedMonitorIds(page.monitorIds);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id, isNew]);

  const toggleMonitor = (monId: string) => {
    setSelectedMonitorIds((prev) =>
      prev.includes(monId) ? prev.filter((m) => m !== monId) : [...prev, monId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        await createStatusPage({ title, slug, monitorIds: selectedMonitorIds, isPublished });
      } else if (id) {
        await updateStatusPage(id, { title, slug, monitorIds: selectedMonitorIds, isPublished });
      }
      navigate('/status-pages');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="animate-pulse bg-surface rounded-lg h-64" />;

  return (
    <div className="max-w-lg mx-auto">
      <button onClick={() => navigate('/status-pages')} className="text-text-secondary hover:text-text-primary text-sm mb-4 inline-block">
        &larr; Back to Status Pages
      </button>
      <h1 className="text-white text-h1 mb-6">{isNew ? 'Create Status Page' : 'Edit Status Page'}</h1>

      <form onSubmit={handleSubmit} className="bg-surface rounded-lg p-6 border border-border space-y-4">
        {error && <div className="bg-status-down/20 text-status-down rounded px-3 py-2 text-sm">{error}</div>}

        <div>
          <label className="block text-text-secondary text-sm mb-1">Title</label>
          <input
            type="text"
            aria-label="Title"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            required
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </div>

        <div>
          <label className="block text-text-secondary text-sm mb-1">Slug</label>
          <input
            type="text"
            aria-label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            pattern="[a-z0-9\-]+"
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <p className="text-text-muted text-xs mt-1">
            Lowercase letters, numbers, and hyphens. Preview:{' '}
            <a href={`/status/${slug || '...'}`} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
              /status/{slug || '...'}
            </a>
          </p>
        </div>

        <div>
          <label className="block text-text-secondary text-sm mb-2">Monitors</label>
          <input
            type="text"
            placeholder="Search monitors…"
            value={monitorSearch}
            onChange={(e) => setMonitorSearch(e.target.value)}
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-brand"
          />
          {selectedMonitorIds.length > 0 && (
            <p className="text-text-muted text-xs mb-1">{selectedMonitorIds.length} selected</p>
          )}
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {filteredMonitors.map((mon) => (
              <label key={mon.id} className="flex items-center gap-2 text-sm text-text-primary cursor-pointer px-2 py-1.5 rounded hover:bg-bg-primary min-h-[44px]">
                <input
                  type="checkbox"
                  checked={selectedMonitorIds.includes(mon.id)}
                  onChange={() => toggleMonitor(mon.id)}
                  className="w-4 h-4 accent-brand"
                />
                {mon.name}
              </label>
            ))}
            {filteredMonitors.length === 0 && monitors.length > 0 && (
              <p className="text-text-muted text-sm">No monitors match "{monitorSearch}"</p>
            )}
            {monitors.length === 0 && (
              <p className="text-text-muted text-sm">No monitors available</p>
            )}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer min-h-[44px]">
          <input
            type="checkbox"
            checked={isPublished}
            onChange={(e) => setIsPublished(e.target.checked)}
            className="w-4 h-4 accent-brand"
          />
          Published (visible to public)
        </label>

        <button
          type="submit"
          disabled={saving}
          className="bg-brand text-white rounded px-4 py-2 text-sm min-h-[44px] disabled:opacity-50 hover:bg-brand-hover transition-colors"
        >
          {saving ? 'Saving...' : isNew ? 'Create' : 'Save'}
        </button>
      </form>
    </div>
  );
}
